/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config";
import BusinessHeader from "../BusinessHeader";
import SidebarAdmin from "../SidebarAdmin";

// Inline styles
const dropzoneStyles = {
  border: "2px dashed #d3d3d3",
  borderRadius: "5px",
  padding: "20px",
  textAlign: "center",
  backgroundColor: "#f9f9f9",
  cursor: "pointer",
  marginBottom: "1rem",
};

const dropzoneHoverStyles = {
  border: "2px dashed #a3a3a3",
  backgroundColor: "#e9e9e9",
};

const selectButtonStyles = {
  backgroundColor: "#7b1fa2",
  color: "white",
  padding: "8px 16px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  marginTop: "10px",
};

const takePhotoLinkStyles = {
  cursor: "pointer",
  color: "#5e6b7a",
  textDecoration: "underline",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
};

const removePhotoLinkStyles = {
  cursor: "pointer",
  color: "#dc3545",
  textDecoration: "underline",
  marginLeft: "10px",
};

const imagePreviewStyles = {
  width: "150px",
  height: "150px",
  borderRadius: "50%",
  objectFit: "cover",
  marginBottom: "1rem",
  border: "2px solid #d3d3d3",
};

const AddAdmin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    user_id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [permissionsData, setPermissionsData] = useState({
    add_department: 0,
    view_doctors_list_own: 0,
    view_doctors_list_all: 0,
    add_doctor: 0,
    add_archive_doctor: 0,
    view_archive_doctors_list: 0,
    reactivate_archive_doctor: 0,
    view_admin_users_own: 0,
    view_admin_users_all: 0,
    add_admin_users: 0,
    view_appointments_own: 0,
    view_appointments_all: 0,
    add_appointment: 0,
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [digitCount, setDigitCount] = useState(0);
  const [requiredDigits, setRequiredDigits] = useState(10);
  const [profileImage, setProfileImage] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [errors, setErrors] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [hasPermission, setHasPermission] = useState(null); // null indicates loading
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      const businessID = localStorage.getItem("BusinessID");
      const userID = localStorage.getItem("userUUID"); // Adjust based on how user_id is stored

      if (!businessID || !userID) {
        console.error("Business ID or User ID not found in localStorage");
        setHasPermission(false);
        return;
      }

      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: businessID, user_id: userID }),
      });

      const data = await response.json();
      if (data.status === "success" && data.permissions) {
        setHasPermission(data.permissions.add_admin_users === 1);
      } else {
        console.error("Failed to fetch permissions:", data.message);
        setHasPermission(false);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setHasPermission(false);
    }
  };

  // Fetch user details by phone
  const fetchUserDetails = async (phone) => {
    if (!phone.trim()) {
      setUserDetails(null);
      setFormData({
        user_id: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
      setProfileImage(null);
      setHasImage(false);
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}get_patient_profile.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (data.user_exists) {
        setUserDetails(data);
        setFormData({
          user_id: data.user_id || "",
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || "",
          phone: data.phone || phone,
        });
        if (data.profile_image) {
          const imageUrl = data.profile_image.startsWith("http")
            ? data.profile_image
            : `${config.API_URL}uploads/profile_images/user_images/${data.profile_image}`;
          setProfileImage(imageUrl);
          setHasImage(true);
        } else {
          setProfileImage(null);
          setHasImage(false);
        }
        fetchAdminPermissions(data.user_id);
      } else {
        setUserDetails(null);
        setFormData({
          user_id: "",
          firstName: "",
          lastName: "",
          email: "",
          phone,
        });
        setProfileImage(null);
        setHasImage(false);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details.");
      setUserDetails(null);
      setProfileImage(null);
      setHasImage(false);
    }
  };

  // Fetch admin permissions
  const fetchAdminPermissions = async (user_id) => {
    const businessId = localStorage.getItem("BusinessID");
    if (!businessId || !user_id) {
      console.error("Missing businessId or user_id:", { businessId, user_id });
      toast.error("Cannot fetch permissions: Missing Business ID or User ID.");
      return;
    }

    try {
      console.log("Fetching permissions with payload:", { user_id, business_id: businessId });
      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, business_id: businessId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Permissions API response:", data);

      if (data.status === "success") {
        setPermissionsData(data.permissions || { ...permissionsData });
      } else {
        console.warn("API returned non-success status:", data.message);
        toast.error(data.message || "Failed to load permissions.");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error(`Failed to fetch permissions: ${error.message}`);
    }
  };

  // Phone validation
  const validatePhoneNumber = (phone, countryCode) => {
    const phoneLength = phone.length;
    switch (countryCode) {
      case "in":
      case "uk":
      case "us":
        return phoneLength === 10;
      case "ae":
      case "lk":
        return phoneLength === 9;
      case "ng":
      case "bd":
        return phoneLength === 10;
      default:
        return false;
    }
  };

  const getRequiredDigits = (countryCode) => {
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
      case "bd":
        return 10;
      case "ae":
      case "lk":
        return 9;
      case "ng":
        return 11;
      default:
        return 10;
    }
  };

  const handlePhoneNumberChange = (value, country) => {
    setPhoneNumber(value);
    const formattedPhone = value.replace(/\D/g, "");
    const digits = value.replace(country.dialCode, "");
    setDigitCount(digits.length);
    const digitsRequired = getRequiredDigits(country.countryCode);
    setRequiredDigits(digitsRequired);
    const isValid = validatePhoneNumber(digits, country.countryCode);
    if (!isValid) {
      setPhoneError(`Invalid phone number length for ${country.name}`);
      setIsPhoneNumberValid(false);
    } else {
      setPhoneError("");
      setIsPhoneNumberValid(true);
      const fullPhone = "+" + formattedPhone;
      setFormData((prev) => ({ ...prev, phone: fullPhone }));
      fetchUserDetails(fullPhone);
    }
  };

  // Input handling
  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "firstName" || name === "lastName") {
      formattedValue = value
        .split(/(\s+|\()/g)
        .map((part, index, arr) =>
          part.length > 0
            ? part === "(" && index < arr.length - 1
              ? "("
              : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : part
        )
        .join("");
    }
    setFormData({ ...formData, [name]: formattedValue });
  };

  // Permission handling
  const handlePermissionChange = (permission) => {
    setPermissionsData((prev) => ({
      ...prev,
      [permission]: prev[permission] ? 0 : 1,
    }));
  };

  const renderPermissions = () => {
    const permissionGroups = {
      Department: ["add_department"],
      Doctors: [
        "view_doctors_list_own",
        "view_doctors_list_all",
        "add_doctor",
        "add_archive_doctor",
        "view_archive_doctors_list",
        "reactivate_archive_doctor",
      ],
      Admins: ["view_admin_users_own", "view_admin_users_all", "add_admin_users"],
      Appointments: ["view_appointments_own", "view_appointments_all", "add_appointment"],
    };

    const permissionPairs = {
      view_doctors_list_own: "view_doctors_list_all",
      view_doctors_list_all: "view_doctors_list_own",
      view_admin_users_own: "view_admin_users_all",
      view_admin_users_all: "view_admin_users_own",
      view_appointments_own: "view_appointments_all",
      view_appointments_all: "view_appointments_own",
    };

    const handleSelectAll = (e) => {
      const isChecked = e.target.checked;
      const updatedPermissions = {};
      Object.keys(permissionsData).forEach((perm) => {
        updatedPermissions[perm] = isChecked ? 1 : 0;
      });
      setPermissionsData(updatedPermissions);
    };

    const allSelected = Object.values(permissionsData).every((value) => value === 1);

    const isPermissionDisabled = (perm) => {
      if (!isPhoneNumberValid || allSelected) return true;
      if (permissionPairs[perm] && permissionsData[permissionPairs[perm]] === 1) return true;
      return false;
    };

    return (
      <div className="form-group">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="mb-0">Set Permissions</h4>
          <div className="form-check">
            <input
              type="checkbox"
              id="selectAll"
              checked={allSelected}
              onChange={handleSelectAll}
              className="form-check-input"
              disabled={!isPhoneNumberValid}
            />
            <label htmlFor="selectAll" className="form-check-label ms-2">
              Select All
            </label>
          </div>
        </div>
        <div
          className="border p-2 rounded mt-2"
          style={{ height: "400px", overflowY: "auto", border: "1px solid #ddd" }}
        >
          {Object.entries(permissionGroups).map(([group, perms]) => (
            <div key={group} className="mb-3">
              <h6>{group}</h6>
              {perms.map((perm) => (
                <div key={perm} className="form-check">
                  <input
                    type="checkbox"
                    id={perm}
                    checked={permissionsData[perm] === 1}
                    onChange={() => handlePermissionChange(perm)}
                    className="form-check-input"
                    disabled={isPermissionDisabled(perm)}
                  />
                  <label htmlFor={perm} className="form-check-label ms-2">
                    {perm.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Profile image handling
  const handleDragOver = (e) => {
    e.preventDefault();
    if (isPhoneNumberValid) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isPhoneNumberValid) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        setProfileImage(file);
        setHasImage(true);
      } else {
        toast.error("Please upload an image file.");
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setProfileImage(file);
      setHasImage(true);
    } else {
      toast.error("Please upload an image file.");
    }
  };

  const handleOpenCamera = async (e) => {
    e.preventDefault();
    if (!isPhoneNumberValid) return;
    setIsCameraOn(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Failed to access camera. Ensure camera permissions are granted.");
      setIsCameraOn(false);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `profile_${Date.now()}.png`, { type: "image/png" });
      setProfileImage(file);
      setHasImage(true);
      stopCamera();
    }, "image/png");
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const handleRemovePhoto = (e) => {
    e.preventDefault();
    if (!isPhoneNumberValid) return;
    setProfileImage(null);
    setHasImage(false);
  };

  // Validation
  const validate = () => {
    let newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First Name is required";
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Invalid email format";
    if (!isPhoneNumberValid) newErrors.phone = "Valid phone number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission) {
      toast.error("You do not have permission to add admins.");
      return;
    }
    if (!validate()) {
      toast.error("Please fix form errors.");
      return;
    }
    const businessId = localStorage.getItem("BusinessID");
    if (!businessId) {
      toast.error("Missing Business ID.");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("business_id", businessId);
    formDataToSend.append("user_id", formData.user_id || "");
    formDataToSend.append("first_name", formData.firstName);
    formDataToSend.append("last_name", formData.lastName);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("phone", formData.phone);
    Object.entries(permissionsData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });
    if (profileImage && profileImage instanceof File) {
      formDataToSend.append("profile_image", profileImage);
    }

    try {
      const response = await fetch(`${config.API_URL}add_admin_permissions.php`, {
        method: "POST",
        body: formDataToSend,
      });
      const result = await response.json();
      if (result.status === "success") {
        toast.success("Admin permissions updated successfully!");
        setFormData({
          user_id: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        });
        setPermissionsData({
          add_department: 0,
          view_doctors_list_own: 0,
          view_doctors_list_all: 0,
          add_doctor: 0,
          add_archive_doctor: 0,
          view_archive_doctors_list: 0,
          reactivate_archive_doctor: 0,
          view_admin_users_own: 0,
          view_admin_users_all: 0,
          add_admin_users: 0,
          view_appointments_own: 0,
          view_appointments_all: 0,
          add_appointment: 0,
        });
        setPhoneNumber("");
        setProfileImage(null);
        setHasImage(false);
        setUserDetails(null);
        navigate("/admin-list");
      } else {
        toast.error(result.message || "Failed to update admin permissions.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to update admin permissions.");
    }
  };

  // Lock fields if user exists
  const isFieldLocked = (fieldName) => {
    return userDetails && ["firstName", "lastName", "email"].includes(fieldName);
  };

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  // Render loading state
  if (hasPermission === null) {
    return (
      <div className="page-wrapper">
        <BusinessHeader />
        <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="add-admin" />
        <div className="content">
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Render access denied
  if (!hasPermission) {
    return (
      <div className="page-wrapper">
        <BusinessHeader />
        <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="add-admin" />
        <div className="content">
          <div className="alert alert-danger">
            <strong>Access Denied</strong>! You do not have permission to add admins.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <BusinessHeader />
      <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="add-admin" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin-list">Admins</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Add Admin</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-8">
                        <h4>Admin Details</h4>
                        <div className="row">
                          <div className="col-12 col-md-6 col-xl-6 mt-3">
                            <div className="form-group local-forms">
                              <label>
                                Phone Number <span className="login-danger">*</span>
                              </label>
                              <PhoneInput
                                onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                                country={"in"}
                                value={phoneNumber}
                                onChange={handlePhoneNumberChange}
                                className="phoneinput"
                                placeholder="Enter phone number"
                                countryCodeEditable={false}
                                masks={{
                                  in: "..........",
                                  gb: "..........",
                                  us: "..........",
                                  ae: ".........",
                                  ng: "..........",
                                  lk: ".........",
                                  bd: "..........",
                                }}
                              />
                              <div className="mt-2 d-flex justify-content-between">
                                {phoneError && <div className="text-danger">{phoneError}</div>}
                                <div>{digitCount}/{requiredDigits}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>
                                First Name <span className="login-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                onKeyPress={(e) => {
                                  if (!/[A-Za-z]/.test(e.key)) e.preventDefault();
                                }}
                                placeholder="Enter first name"
                                disabled={!isPhoneNumberValid || isFieldLocked("firstName")}
                                readOnly={isFieldLocked("firstName")}
                              />
                              {errors.firstName && (
                                <div className="invalid-feedback">{errors.firstName}</div>
                              )}
                            </div>
                          </div>
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>
                                Last Name <span className="login-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                onKeyPress={(e) => {
                                  if (!/[A-Za-z]/.test(e.key)) e.preventDefault();
                                }}
                                placeholder="Enter last name"
                                disabled={!isPhoneNumberValid || isFieldLocked("lastName")}
                                readOnly={isFieldLocked("lastName")}
                              />
                              {errors.lastName && (
                                <div className="invalid-feedback">{errors.lastName}</div>
                              )}
                            </div>
                          </div>
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>
                                Email <span className="login-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email"
                                disabled={!isPhoneNumberValid || isFieldLocked("email")}
                                readOnly={isFieldLocked("email")}
                              />
                              {errors.email && (
                                <div className="invalid-feedback">{errors.email}</div>
                              )}
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-group local-forms">
                              <label>Profile Image (Optional)</label>
                              {!isCameraOn && !hasImage ? (
                                <div
                                  style={{
                                    ...dropzoneStyles,
                                    ...(isDragging ? dropzoneHoverStyles : {}),
                                    ...(!isPhoneNumberValid ? { pointerEvents: "none", opacity: 0.6 } : {}),
                                  }}
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                  onClick={() => isPhoneNumberValid && fileInputRef.current.click()}
                                >
                                  <p>
                                    {profileImage ? `Selected: ${profileImage.name || "Image"}` : "Drag and drop image"}
                                  </p>
                                  {!profileImage && (
                                    <>
                                      <p>OR</p>
                                      <button style={selectButtonStyles} type="button" disabled={!isPhoneNumberValid}>
                                        SELECT
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : isCameraOn ? (
                                <div>
                                  <video
                                    ref={videoRef}
                                    autoPlay
                                    style={{ width: "100%", maxHeight: "200px" }}
                                  ></video>
                                  <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-primary me-2"
                                      onClick={handleCapturePhoto}
                                      disabled={!isPhoneNumberValid}
                                    >
                                      Capture
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={stopCamera}
                                      disabled={!isPhoneNumberValid}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <img
                                    src={
                                      profileImage instanceof File
                                        ? URL.createObjectURL(profileImage)
                                        : profileImage
                                    }
                                    alt="Profile Preview"
                                    style={imagePreviewStyles}
                                  />
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                disabled={!isPhoneNumberValid}
                              />
                              <div className="d-flex justify-content-between">
                                <a
                                  href="#"
                                  style={takePhotoLinkStyles}
                                  onClick={handleOpenCamera}
                                  className={!isPhoneNumberValid ? "disabled" : ""}
                                >
                                  <FeatherIcon icon="camera" />
                                  Take a Photo
                                </a>
                                {hasImage && (
                                  <a
                                    href="#"
                                    style={removePhotoLinkStyles}
                                    onClick={handleRemovePhoto}
                                    className={!isPhoneNumberValid ? "disabled" : ""}
                                  >
                                    Remove Photo
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        {renderPermissions()}
                      </div>
                      <div className="col-12">
                        <div className="doctor-submit text-end">
                          <button
                            type="submit"
                            className="btn btn-primary submit-form me-2"
                            disabled={
                              !isPhoneNumberValid ||
                              !formData.firstName ||
                              !formData.lastName ||
                              !formData.email
                            }
                          >
                            Submit
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary cancel-form"
                            onClick={() => navigate("/admin-list")}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAdmin;